import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Edit2,
  RotateCcw,
  Send,
  Check,
  X,
  Loader2,
  AlertCircle,
  Settings,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminSystemEmails = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  
  const [editForm, setEditForm] = useState({
    subject: "",
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/system-email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error("Failed to load email templates");
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      subject: template.subject || template.default_subject,
      is_active: template.is_active
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(
        `${API}/admin/system-email-templates/${selectedTemplate.template_key}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Template updated");
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to update template");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (template) => {
    if (!confirm(`Reset "${template.name}" to default settings?`)) return;
    
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(
        `${API}/admin/system-email-templates/${template.template_key}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Template reset to default");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to reset template");
    }
  };

  const openTestDialog = (template) => {
    setSelectedTemplate(template);
    setTestEmail("");
    setTestDialogOpen(true);
  };

  const handleSendTest = async () => {
    if (!selectedTemplate || !testEmail) return;
    
    setSendingTest(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API}/admin/system-email-templates/${selectedTemplate.template_key}/test`,
        { email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Test email sent to ${testEmail}`);
      setTestDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const getTriggerLabel = (trigger) => {
    const labels = {
      signup: "On Signup",
      booking_confirmed: "Booking Confirmed",
      quote_created: "Quote Created",
      payment_completed: "Payment Received",
      gallery_delivered: "Gallery Delivered",
      trial_ending: "Trial Ending"
    };
    return labels[trigger] || trigger;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-system-emails">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif flex items-center gap-3">
            <Mail className="w-6 h-6 text-primary" />
            System Email Templates
          </h1>
          <p className="text-muted-foreground mt-1">
            Customize automated emails sent by the platform
          </p>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid gap-4">
        {templates.map((template) => (
          <motion.div
            key={template.template_key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-border hover:border-primary/30 transition-colors">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{template.name}</h3>
                      {template.has_custom_override && (
                        <Badge variant="outline" className="text-xs">
                          Customized
                        </Badge>
                      )}
                      {!template.is_active && (
                        <Badge variant="destructive" className="text-xs">
                          Disabled
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {template.description}
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <Badge variant="secondary" className="font-normal">
                        Trigger: {getTriggerLabel(template.trigger)}
                      </Badge>
                      <Badge variant="secondary" className="font-normal">
                        Subject: {template.subject}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openTestDialog(template)}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Test
                    </Button>
                    {template.has_custom_override && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReset(template)}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reset
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => openEditDialog(template)}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>
                
                {/* Variables */}
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Available Variables:</p>
                  <div className="flex flex-wrap gap-1">
                    {template.variables?.map((v) => (
                      <code key={v} className="text-xs px-2 py-0.5 bg-muted rounded">
                        {`{{${v}}}`}
                      </code>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {selectedTemplate?.name}</DialogTitle>
            <DialogDescription>
              Customize the email subject and settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="subject">Email Subject</Label>
              <Input
                id="subject"
                value={editForm.subject}
                onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                placeholder="Enter email subject..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Default: {selectedTemplate?.default_subject}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="is_active">Enabled</Label>
                <p className="text-xs text-muted-foreground">
                  When disabled, this email will not be sent
                </p>
              </div>
              <Switch
                id="is_active"
                checked={editForm.is_active}
                onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test "{selectedTemplate?.name}" email
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="test-email">Email Address</Label>
            <Input
              id="test-email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email address..."
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendTest} 
              disabled={sendingTest || !testEmail}
            >
              {sendingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSystemEmails;
